import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Lock, Mail, Building, Sparkles } from 'lucide-react';
import api from '../utils/api';

const Login = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const response = await api.post(endpoint, formData);

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      toast.success(isRegister ? 'Registro exitoso' : 'Inicio de sesion exitoso');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al procesar la solicitud';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex"
      style={{ background: '#0B132B' }}
    >
      {/* Tech grid background */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(rgba(58, 134, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(58, 134, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Left side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0F1A36 0%, #0B132B 100%)'
        }}
      >
        {/* Glow effects */}
        <div 
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(58, 134, 255, 0.15) 0%, transparent 70%)'
          }}
        />
        <div 
          className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%)'
          }}
        />

        <div className="relative z-10 p-12 flex flex-col justify-center h-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-lg"
          >
            {/* Logo */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 blur-xl bg-[#00D4FF]/40 rounded-full" />
                <svg viewBox="0 0 40 40" className="w-full h-full relative z-10">
                  <defs>
                    <linearGradient id="loginGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3A86FF" />
                      <stop offset="50%" stopColor="#00D4FF" />
                      <stop offset="100%" stopColor="#00F5FF" />
                    </linearGradient>
                  </defs>
                  <path 
                    d="M20 2 L38 20 L20 38 L2 20 Z" 
                    fill="none" 
                    stroke="url(#loginGradient)" 
                    strokeWidth="2"
                  />
                  <path 
                    d="M20 8 L32 20 L20 32 L8 20 Z" 
                    fill="url(#loginGradient)" 
                    opacity="0.3"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white tracking-wider">
                  PERITUM
                </h1>
                <p className="text-[#00D4FF]/70 tracking-[0.25em] text-xs uppercase">
                  Defensa Corporativa y Tributaria
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-4">
              Sistema ERP <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3A86FF] to-[#00D4FF]">Inteligente</span>
            </h2>
            
            <p className="text-white/60 text-lg leading-relaxed mb-8">
              Plataforma integral de gestion empresarial con inteligencia artificial para optimizar tus operaciones.
            </p>

            {/* Features list */}
            <div className="space-y-4">
              {[
                'Cerebro de Tareas con IA',
                'Dashboard ejecutivo en tiempo real',
                'Gestion financiera integrada',
                'Reportes automatizados'
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + idx * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-[#3A86FF]/20 flex items-center justify-center border border-[#3A86FF]/30">
                    <Sparkles className="w-3 h-3 text-[#00D4FF]" />
                  </div>
                  <span className="text-white/80">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          <Card 
            className="p-8 border border-[#3A86FF]/20 backdrop-blur-sm"
            style={{ background: 'rgba(22, 32, 66, 0.8)' }}
          >
            <div className="text-center mb-8">
              <div 
                className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)',
                  border: '1px solid rgba(58, 134, 255, 0.3)'
                }}
              >
                <Building className="w-7 h-7 text-[#00D4FF]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {isRegister ? 'Crear Cuenta' : 'Iniciar Sesion'}
              </h2>
              <p className="text-white/50 text-sm">
                Solo para emails @peritum.cl
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {isRegister && (
                <div>
                  <Label htmlFor="name" className="text-white/70 text-sm">
                    Nombre Completo
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Juan Perez"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={isRegister}
                    className="mt-2 bg-[#0B132B] border-[#3A86FF]/20 text-white placeholder:text-white/30 focus:border-[#3A86FF]/50 focus:ring-[#3A86FF]/20"
                    data-testid="name-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-white/70 text-sm">
                  Email
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3A86FF]/60" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu.email@peritum.cl"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="pl-10 bg-[#0B132B] border-[#3A86FF]/20 text-white placeholder:text-white/30 focus:border-[#3A86FF]/50 focus:ring-[#3A86FF]/20"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-white/70 text-sm">
                  Contrasena
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3A86FF]/60" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="********"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pl-10 bg-[#0B132B] border-[#3A86FF]/20 text-white placeholder:text-white/30 focus:border-[#3A86FF]/50 focus:ring-[#3A86FF]/20"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 text-base font-semibold text-white transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #3A86FF 0%, #00D4FF 100%)',
                  boxShadow: '0 4px 20px rgba(58, 134, 255, 0.3)'
                }}
                data-testid="submit-btn"
              >
                {loading
                  ? 'Procesando...'
                  : isRegister
                  ? 'Registrarse'
                  : 'Iniciar Sesion'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-[#00D4FF]/80 hover:text-[#00D4FF] transition-colors"
                data-testid="toggle-auth-mode-btn"
              >
                {isRegister
                  ? 'Ya tienes cuenta? Inicia sesion'
                  : 'No tienes cuenta? Registrate'}
              </button>
            </div>
          </Card>

          {/* Footer */}
          <p className="text-center text-white/30 text-xs mt-6">
            By Mbat - Sistema ERP Peritum
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
